import { motion } from "framer-motion";
import { User, Store, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { CUSTOMER_APP_URL, VENDOR_APP_URL } from "@/config/appConfig";
import video from "@/assets/videos/6894121-uhd_3840_2160_25fps.mp4";

const ToggleCustomerorBusiness = () => {
  const navigate = useNavigate();

  const options = [
    {
      title: "I am a Customer",
      description: "Order fresh food and save money while reducing waste.",
      icon: <User className="w-12 h-12" />,
      path: `${CUSTOMER_APP_URL}/customer`,
      color: "from-green-500 to-green-700",
      hoverColor: "hover:shadow-green-500/20",
    },
    {
      title: "I am a Vendor",
      description: "List your surplus food and reach more customers.",
      icon: <Store className="w-12 h-12" />,
      path: VENDOR_APP_URL,
      color: "from-amber-600 to-amber-800",
      hoverColor: "hover:shadow-amber-500/20",
    },
  ];

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-slate-900 font-outfit">
      {/* Background Video with Overlay */}
      <div className="absolute inset-0 z-0">
        <video
          className="w-full h-full object-cover opacity-30"
          autoPlay
          muted
          loop
          playsInline
        >
          <source src={video} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-linear-to-b from-black/60 via-black/40 to-black/60" />
      </div>

      {/* Back Button */}
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={() => navigate("/")}
        className="absolute top-8 left-8 z-20 flex items-center gap-2 text-white/70 hover:text-white transition-colors group"
      >
        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
        Back to Home
      </motion.button>

      <div className="relative z-10 max-w-5xl w-full px-6 py-12">
        <div className="text-center mb-16">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight"
          >
            How would you like to <span className="text-green-400">Join Us?</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg text-white/60 max-w-2xl mx-auto"
          >
            Choose your account type to continue your journey with Smartly Orders.
          </motion.p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {options.map((option, index) => (
            <motion.div
              key={option.title}
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ delay: index * 0.2 }}
              whileHover={{ y: -8 }}
              onClick={() => option.path.startsWith('http') ? window.location.href = option.path : navigate(option.path)}
              className={`relative cursor-pointer group rounded-3xl p-8 lg:p-10 border border-white/10 bg-white/5 backdrop-blur-xl transition-all duration-500 hover:bg-white/10 ${option.hoverColor} shadow-2xl overflow-hidden`}
            >
              {/* Decorative Background Gradient */}
              <div className={`absolute -top-24 -right-24 w-64 h-64 bg-linear-to-br ${option.color} opacity-0 group-hover:opacity-10 blur-3xl transition-opacity duration-500`} />
              
              <div className="relative z-10 flex flex-col h-full">
                <div className={`w-20 h-20 rounded-2xl bg-linear-to-br ${option.color} flex items-center justify-center text-white mb-8 group-hover:scale-110 transition-transform duration-500 shadow-lg`}>
                  {option.icon}
                </div>
                
                <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4 group-hover:text-green-400 transition-colors">
                  {option.title}
                </h2>
                
                <p className="text-white/60 text-lg leading-relaxed mb-8 grow">
                  {option.description}
                </p>

                <div className="flex items-center gap-2 text-white font-semibold group-hover:gap-4 transition-all">
                  Get Started
                  <span className="text-xl">→</span>
                </div>
              </div>

              {/* Shine effect */}
              <div className="absolute inset-0 bg-linear-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ToggleCustomerorBusiness;
